import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { useModuleSession } from "@/contexts/ModuleSessionContext";

interface ImpersonatedTenant {
  id: string;
  name: string;
}

interface ImpersonationContextType {
  impersonatedTenant: ImpersonatedTenant | null;
  isImpersonating: boolean;
  startImpersonation: (tenant: ImpersonatedTenant) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const STORAGE_KEY = "impersonated_tenant";

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { currentModule } = useModuleSession();
  const [impersonatedTenant, setImpersonatedTenant] = useState<ImpersonatedTenant | null>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const prevModuleRef = useRef(currentModule);

  const startImpersonation = useCallback((tenant: ImpersonatedTenant) => {
    setImpersonatedTenant(tenant);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tenant));
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedTenant(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Impersonazione vale solo sul Core: uscendo da core si ripulisce
  useEffect(() => {
    const prev = prevModuleRef.current;
    prevModuleRef.current = currentModule;
    if (
      impersonatedTenant &&
      prev === "core" &&
      currentModule !== null &&
      currentModule !== "core"
    ) {
      stopImpersonation();
    }
  }, [currentModule, impersonatedTenant, stopImpersonation]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedTenant,
        isImpersonating: !!impersonatedTenant,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}
